import {
  DataSnapshot,
  InferenceProfile,
  ModelOffering,
  DataResidencyScope,
  InferenceOption,
  RegionAssuranceClass,
  withResidencyBoundary,
} from '@bedrock-profiles/shared';
import { extractCRISRegion } from './crisRegion';

const GLOBAL_JURISDICTIONS = ['Global', 'US', 'EU', 'APAC', 'Japan', 'Australia', 'Canada'];
const APAC_JURISDICTION_VARIANTS = ['APAC', 'Japan', 'Australia'];

function getJurisdiction(regionCode: string): string {
  const normalized = regionCode.toLowerCase();

  if (
    normalized === 'us' ||
    normalized.startsWith('us-') ||
    normalized.startsWith('sa-') ||
    normalized.startsWith('eastus') ||
    normalized.startsWith('westus') ||
    normalized.startsWith('centralus') ||
    normalized.startsWith('northcentralus') ||
    normalized.startsWith('southcentralus') ||
    normalized.startsWith('ca-')
  ) return 'US';

  if (normalized === 'united-states' || normalized === 'north-america') return 'US';

  if (
    normalized === 'eu' ||
    normalized === 'europe' ||
    normalized.startsWith('eu-') ||
    normalized === 'westeurope' ||
    normalized === 'northeurope' ||
    normalized === 'swedencentral' ||
    normalized === 'francecentral' ||
    normalized === 'germanywestcentral' ||
    normalized === 'uksouth' ||
    normalized === 'switzerlandnorth'
  ) return 'EU';

  if (
    normalized === 'australia' ||
    normalized === 'australiaeast' ||
    normalized === 'australiasoutheast' ||
    normalized === 'ap-southeast-2' ||
    normalized === 'ap-southeast-4'
  ) return 'Australia';

  if (
    normalized === 'japan' ||
    normalized === 'japaneast' ||
    normalized === 'japanwest' ||
    normalized === 'ap-northeast-1' ||
    normalized === 'ap-northeast-3'
  ) return 'Japan';

  if (normalized.startsWith('ap-') || normalized === 'asia' || normalized.startsWith('asia')) return 'APAC';
  if (normalized === 'asia-pacific' || normalized === 'apac') return 'APAC';
  return 'Unknown';
}

function detectCrossBoundaryRoutes(
  sourceRegion: string | null | undefined,
  destinationRegions: string[] | null | undefined
): string[] {
  if (!sourceRegion || !destinationRegions || destinationRegions.length === 0) {
    return [];
  }

  const source = getJurisdiction(sourceRegion);
  const routes = new Set<string>();

  destinationRegions.forEach((destinationRegion) => {
    const destination = getJurisdiction(destinationRegion);
    if (destination !== source) {
      routes.add(`${source}->${destination}`);
    }
  });

  return Array.from(routes);
}

function computeResidencyScope(profile: InferenceProfile): DataResidencyScope {
  if (profile.profileId.startsWith('global.') || profile.regionalGroup === 'Global') {
    return 'global';
  }

  const regions = [
    ...(profile.sourceRegion ? [profile.sourceRegion] : []),
    ...(profile.destinationRegions || []),
  ];

  if (regions.length <= 1) return 'single-region';

  const jurisdictions = new Set(regions.map((region) => getJurisdiction(region)));
  if (jurisdictions.size === 1 && !jurisdictions.has('Unknown')) return 'geo-boundary';
  return 'cross-region';
}

function collapseAPACJurisdictions(jurisdictions: string[]): string[] {
  const hasMultipleAPAC = jurisdictions.filter((j) => APAC_JURISDICTION_VARIANTS.includes(j)).length > 1;
  if (hasMultipleAPAC) {
    return [...jurisdictions.filter((j) => !APAC_JURISDICTION_VARIANTS.includes(j)), 'APAC'];
  }
  return jurisdictions;
}

function buildJurisdictions(profile: InferenceProfile, residencyScope: DataResidencyScope): string[] {
  if (residencyScope === 'global') {
    return GLOBAL_JURISDICTIONS;
  }

  const detected = Array.from(new Set([
    ...(profile.sourceRegion ? [getJurisdiction(profile.sourceRegion)] : []),
    ...(profile.destinationRegions || []).map((region) => getJurisdiction(region)),
  ]));

  if (detected.length === 0) {
    return ['Unknown'];
  }

  return collapseAPACJurisdictions(detected);
}

function inferInferenceOptions(
  profile: InferenceProfile,
  deploymentType: ModelOffering['deployment']['deploymentType']
): InferenceOption[] {
  const options = new Set<InferenceOption>();

  if (deploymentType === 'single-region-endpoint') {
    options.add('direct-invoke');
  } else if (deploymentType === 'inference-profile') {
    options.add('inference-profile-routing');
  } else if (deploymentType === 'global-endpoint') {
    options.add('global-routing');
  }

  const providerSpecific = profile.providerSpecific as {
    inferenceTypesSupported?: unknown;
    responseStreamingSupported?: unknown;
  } | undefined;

  const inferenceTypes = Array.isArray(providerSpecific?.inferenceTypesSupported)
    ? providerSpecific.inferenceTypesSupported
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.toUpperCase().replace(/[\s-]+/g, '_'))
    : [];

  if (inferenceTypes.includes('ON_DEMAND')) options.add('direct-invoke');
  if (inferenceTypes.includes('INFERENCE_PROFILE')) options.add('inference-profile-routing');
  if (inferenceTypes.includes('PROVISIONED') || inferenceTypes.includes('PROVISIONED_THROUGHPUT')) {
    options.add('provisioned-throughput');
  }
  if (inferenceTypes.includes('BATCH')) {
    options.add('batch-inference');
    options.add('async-inference');
  }
  if (inferenceTypes.includes('ASYNC')) options.add('async-inference');

  if (providerSpecific?.responseStreamingSupported === true) {
    options.add('streaming-inference');
  }

  if (/(video|reel|canvas|luma\.ray|pegasus|speech|audio|image)/i.test(profile.modelName)) {
    options.add('async-inference');
  }

  return Array.from(options).sort();
}

function inferRegionAssuranceClass(
  profile: InferenceProfile,
  residencyScope: DataResidencyScope,
  jurisdictions: string[],
  deploymentType: ModelOffering['deployment']['deploymentType']
): RegionAssuranceClass {
  if (jurisdictions.includes('Unknown')) return 'unknown';

  if (deploymentType === 'global-endpoint' || residencyScope === 'global' || residencyScope === 'cross-region') {
    return 'global-routing';
  }

  if (residencyScope === 'geo-boundary') return 'geography-boundary';

  if (residencyScope === 'single-region') {
    const source = (profile.sourceRegion || '').trim().toLowerCase();
    if (/^[a-z]{2}-[a-z0-9-]+-\d$/.test(source)) return 'strict-region';
    if (source) return 'geography-boundary';
  }

  return 'unknown';
}

export function profileToModelOffering(profile: InferenceProfile): ModelOffering {
  const offering = profileToStoredOffering(profile);
  return { ...offering, residency: withResidencyBoundary(offering) };
}

function profileToStoredOffering(profile: InferenceProfile): ModelOffering {
  const residencyScope = computeResidencyScope(profile);
  const jurisdictions = buildJurisdictions(profile, residencyScope);
  const crossBoundaryRoutes = detectCrossBoundaryRoutes(profile.sourceRegion, profile.destinationRegions);
  const deploymentType: ModelOffering['deployment']['deploymentType'] =
    profile.profileId.startsWith('global.') || profile.regionalGroup === 'Global'
    ? 'global-endpoint'
    : (!profile.destinationRegions || profile.destinationRegions.length <= 1)
      ? 'single-region-endpoint'
      : 'inference-profile';
  const inferenceOptions = inferInferenceOptions(profile, deploymentType);
  const assuranceClass = inferRegionAssuranceClass(profile, residencyScope, jurisdictions, deploymentType);

  const crisRegion = extractCRISRegion(profile.profileId);
  const correctedRegionalGroup = crisRegion === 'Global'
    ? 'Global'
    : (crisRegion && profile.regionalGroup === 'Global')
      ? crisRegion
      : profile.regionalGroup;

  return {
    offeringId: profile.profileId,
    displayName: profile.profileName,
    model: {
      modelId: profile.modelName,
      modelName: profile.modelName,
      provider: profile.provider,
      publisher: profile.modelPublisher ?? profile.provider,
      publisherName: profile.modelPublisher ?? profile.provider,
    },
    deployment: {
      deploymentId: profile.profileId,
      deploymentType,
      cloudProvider: profile.cloudProvider,
      inferenceProvider: profile.inferenceProvider ?? profile.cloudProvider,
      inferenceProviderName: profile.inferenceProvider ?? profile.cloudProvider,
      sourceRegion: profile.sourceRegion,
      destinationRegions: profile.destinationRegions,
      regionalGroup: correctedRegionalGroup,
    },
    status: profile.status,
    description: profile.description,
    createdAt: profile.createdAt,
    lastUpdated: profile.lastUpdated,
    modelLifecycleStatus: profile.modelLifecycleStatus,
    modelLifecycleUpdatedAt: profile.modelLifecycleUpdatedAt,
    deprecationNotice: profile.deprecationNotice,
    pricingModelName: profile.modelName,
    inferenceOptions,
    residency: {
      scope: residencyScope,
      jurisdictions,
      crossBoundaryRoutes: crossBoundaryRoutes.length > 0 ? crossBoundaryRoutes : undefined,
      assuranceClass,
      evidence: {
        confidence: profile.cloudProvider === 'AWS Bedrock' ? 'provider-documented' : 'inferred',
        summary:
          profile.cloudProvider === 'AWS Bedrock'
            ? 'Derived from provider-published routing behavior and profile metadata.'
            : 'Derived from available region metadata in source payloads.',
      },
    },
    providerSpecific: profile.providerSpecific,
    // Preserve original AWS profile ID (with ":0" suffix) for backward-compatible URL matching
    legacyProfileId: profile.cloudProvider === 'AWS Bedrock' && !profile.profileId.includes(':')
      ? `${profile.profileId}:0`
      : profile.profileId,
  };
}

export function offeringToProfile(offering: ModelOffering): InferenceProfile {
  return {
    profileId: offering.legacyProfileId || offering.offeringId,
    profileName: offering.displayName,
    cloudProvider: offering.deployment.cloudProvider,
    profileArn: typeof offering.providerSpecific?.inferenceProfileArn === 'string'
      ? offering.providerSpecific.inferenceProfileArn
      : offering.offeringId,
    provider: offering.model.provider,
    modelPublisher: offering.model.publisher ?? offering.model.provider,
    modelName: offering.pricingModelName || offering.model.modelId,
    description: offering.description,
    sourceRegion: offering.deployment.sourceRegion,
    destinationRegions: offering.deployment.destinationRegions,
    inferenceProvider: offering.deployment.inferenceProvider ?? offering.deployment.cloudProvider,
    status: offering.status,
    createdAt: offering.createdAt,
    lastUpdated: offering.lastUpdated,
    regionalGroup: offering.deployment.regionalGroup as InferenceProfile['regionalGroup'],
    modelLifecycleStatus: offering.modelLifecycleStatus,
    modelLifecycleUpdatedAt: offering.modelLifecycleUpdatedAt,
    deprecationNotice: offering.deprecationNotice,
    providerSpecific: offering.providerSpecific,
  };
}

/**
 * Normalise a stored offering for display. Residency boundary fields are
 * derived from region codes here so archived rows get them too.
 */
function normalizeModelOffering(offering: ModelOffering): ModelOffering {
  return {
    ...offering,
    residency: withResidencyBoundary(offering),
    model: {
      ...offering.model,
      publisher: offering.model.publisher ?? offering.model.provider,
      publisherName: offering.model.publisherName ?? offering.model.publisher ?? offering.model.provider,
    },
    deployment: {
      ...offering.deployment,
      inferenceProvider: offering.deployment.inferenceProvider ?? offering.deployment.cloudProvider,
      inferenceProviderName:
        offering.deployment.inferenceProviderName ??
        offering.deployment.inferenceProvider ??
        offering.deployment.cloudProvider,
    },
  };
}

export function getModelOfferings(snapshot?: DataSnapshot): ModelOffering[] {
  if (!snapshot) return [];
  if (snapshot.modelOfferings && snapshot.modelOfferings.length > 0) {
    return snapshot.modelOfferings.map(normalizeModelOffering);
  }
  return snapshot.profiles.map(profileToModelOffering);
}

const INTERNAL_MODEL_ID_TOKENS = new Set([
  'test',
  'tests',
  'testing',
  'internal',
  'dummy',
  'sandbox',
  'fake',
  'mock',
  'tmp',
  'temp',
]);

/**
 * Provider catalogues occasionally list internal or test models (for example
 * GCP's "pt-test"). Matching whole ID tokens keeps names such as "latest" or
 * "contest" visible. These rows stay in the data; listing views hide them.
 */
export function isInternalTestOffering(offering: Pick<ModelOffering, 'model'>): boolean {
  const tokens = String(offering.model?.modelId ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return tokens.some((token) => INTERNAL_MODEL_ID_TOKENS.has(token));
}
